/**
 * Player names: chosen or generated.
 *
 * Shared by client and server so the rules that decide whether a name is
 * acceptable exist once. The client uses them for instant feedback; the server
 * re-applies them because a client is not a source of truth.
 */

export const MIN_NAME_LENGTH = 2;
/**
 * Names sit beside Twitch handles on a stream overlay and inside a 44px-tall
 * seat row. Twitch's own limit is 25; going much beyond that starts wrapping.
 */
export const MAX_NAME_LENGTH = 24;

/**
 * Characters a name may contain.
 *
 * Deliberately permissive about scripts — Twitch display names are routinely
 * CJK or Cyrillic and rejecting them would be its own bug — but strict about
 * the things that break rendering or enable impersonation:
 *
 *  - no control characters,
 *  - no bidirectional overrides (RLO can render "yekcoJ" as a different name),
 *  - no zero-width or invisible characters (two visually identical names),
 *  - no combining marks stacked into other rows ("Zalgo"),
 *  - no runs of whitespace.
 */
const FORBIDDEN = new RegExp(
  [
    '[\\u0000-\\u001F\\u007F-\\u009F]', // control
    '[\\u200B-\\u200F\\u202A-\\u202E\\u2066-\\u2069]', // zero-width, bidi
    '[\\uFEFF\\u00AD]', // BOM, soft hyphen
    '[\\u0300-\\u036F]{3,}', // stacked combining marks
  ].join('|'),
  'u',
);

export type NameRejection =
  | 'too_short'
  | 'too_long'
  | 'forbidden_characters'
  | 'reserved';

/**
 * Names nobody may take, because they would misrepresent who is speaking.
 * `system` is what the chat itself posts under.
 */
const RESERVED = new Set(['system', 'server', 'admin', 'moderator', 'mod', 'claude', 'bot']);

/** Collapses whitespace and trims. Applied before every check and before storing. */
export function normalizeName(raw: string): string {
  return raw.normalize('NFKC').replace(/\s+/g, ' ').trim();
}

export type NameCheck =
  | { readonly ok: true; readonly name: string }
  | { readonly ok: false; readonly reason: NameRejection };

export function checkName(raw: string): NameCheck {
  const name = normalizeName(raw);

  // Length is counted in code points, not UTF-16 units, so an emoji or a
  // non-BMP character counts as one character rather than two.
  const length = [...name].length;
  if (length < MIN_NAME_LENGTH) return { ok: false, reason: 'too_short' };
  if (length > MAX_NAME_LENGTH) return { ok: false, reason: 'too_long' };
  if (FORBIDDEN.test(name)) return { ok: false, reason: 'forbidden_characters' };
  if (RESERVED.has(name.toLowerCase())) return { ok: false, reason: 'reserved' };

  return { ok: true, name };
}

export function nameRejectionMessage(reason: NameRejection): string {
  switch (reason) {
    case 'too_short':
      return `Names need at least ${MIN_NAME_LENGTH} characters.`;
    case 'too_long':
      return `Names can be at most ${MAX_NAME_LENGTH} characters.`;
    case 'forbidden_characters':
      return 'That name uses characters that cannot be displayed.';
    case 'reserved':
      return 'That name is reserved.';
  }
}
